import React, { useState, useMemo, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { cn } from "@/lib/utils";
import { Search, RefreshCw, MousePointer, ShoppingCart, Eye, AlertTriangle, Webhook, Calendar, Download, Edit2, X, Check } from "lucide-react";

function formatDateTime(date: string | Date): string {
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric", month: "short", day: "numeric",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).format(new Date(date));
}

type TabType = "clicks" | "conversions" | "impressions" | "invalid_clicks" | "postback_logs" | "events";

function getDefaultDates() {
  const to = new Date();
  to.setDate(to.getDate() + 1);
  const from = new Date();
  from.setDate(from.getDate() - 30);
  return { from: from.toISOString().split("T")[0], to: to.toISOString().split("T")[0] };
}

export function RawReportPage() {
  const qc = useQueryClient();
  const defaults = useMemo(() => getDefaultDates(), []);
  const [activeTab, setActiveTab] = useState<TabType>("clicks");
  const [dateFrom, setDateFrom] = useState(defaults.from);
  const [dateTo, setDateTo] = useState(defaults.to);
  const [offerId, setOfferId] = useState("");
  const [affiliateId, setAffiliateId] = useState("");
  const [status, setStatus] = useState("");
  const [transactionId, setTransactionId] = useState("");
  const [limit] = useState(100);

  // Conversion update dialog
  const [editingConversion, setEditingConversion] = useState<Record<string, any> | null>(null);
  const [updateForm, setUpdateForm] = useState({ status: "", payout: "", revenue: "", saleAmount: "", reason: "" });

  const { data: offersData } = useQuery({
    queryKey: ["offers-list-all"],
    queryFn: async () => {
      const res = await api.get("/v1/offers", { pageSize: "500" });
      if (!res.ok) return { data: [] };
      return res.json() as Promise<{ data: Array<{ id: number; name: string }> }>;
    },
    staleTime: 60_000,
  });

  const { data: affiliatesData } = useQuery({
    queryKey: ["affiliates-list-all"],
    queryFn: async () => {
      const res = await api.get("/v1/affiliates", { pageSize: "500" });
      if (!res.ok) return { data: [] };
      return res.json() as Promise<{ data: Array<{ id: number; name: string }> }>;
    },
    staleTime: 60_000,
  });

  const offersList = offersData?.data ?? [];
  const affiliatesList = affiliatesData?.data ?? [];

  const endpointMap: Record<TabType, string> = {
    clicks: "/v1/reporting/clicks",
    conversions: "/v1/reporting/conversions",
    impressions: "/v1/reporting/impressions",
    invalid_clicks: "/v1/reporting/invalid-clicks",
    postback_logs: "/v1/reporting/postback-logs",
    events: "/v1/reporting/events",
  };

  const queryPayload = useMemo(() => {
    const base: Record<string, unknown> = { from: dateFrom, to: dateTo, limit, offset: 0 };
    if (offerId) base.offerId = Number(offerId);
    if (affiliateId) base.affiliateId = Number(affiliateId);
    if (activeTab === "conversions") {
      if (status) base.status = status;
      if (transactionId) base.transactionId = transactionId;
    }
    if (activeTab === "postback_logs" && transactionId) base.transactionId = transactionId;
    return base;
  }, [dateFrom, dateTo, offerId, affiliateId, status, transactionId, activeTab, limit]);

  const { data: result, isLoading, error, refetch } = useQuery({
    queryKey: ["raw-reporting", activeTab, queryPayload],
    queryFn: async () => {
      const res = await api.post(endpointMap[activeTab], queryPayload);
      if (!res.ok) {
        const err = await res.json().catch(() => ({})) as Record<string, string>;
        throw new Error(err.error || `Failed to fetch ${activeTab}`);
      }
      return res.json() as Promise<{ data: Record<string, unknown>[]; rows: number }>;
    },
  });

  const data = result?.data ?? [];
  const totalRows = result?.rows ?? 0;

  const handleExport = useCallback(async () => {
    const res = await api.post(endpointMap[activeTab], { ...queryPayload, format: "csv" });
    if (!res.ok) return;
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${activeTab}_export.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }, [activeTab, queryPayload]);

  const updateMutation = useMutation({
    mutationFn: async (payload: { transactionId: string; status: string; payout?: number; revenue?: number; saleAmount?: number; reason?: string }) => {
      const res = await api.post("/v1/conversions", payload);
      if (!res.ok) throw new Error("Failed to update conversion");
      return res.json();
    },
    onSuccess: () => {
      setEditingConversion(null);
      qc.invalidateQueries({ queryKey: ["raw-reporting"] });
    },
  });

  function openUpdateDialog(row: Record<string, any>) {
    setEditingConversion(row);
    setUpdateForm({
      status: String(row.status || "approved"),
      payout: String(row.payout ?? ""),
      revenue: String(row.revenue ?? ""),
      saleAmount: String(row.sale_amount ?? ""),
      reason: "",
    });
  }

  function submitUpdate() {
    if (!editingConversion) return;
    const payload: Record<string, unknown> = {
      transactionId: editingConversion.transaction_id,
      status: updateForm.status,
    };
    if (updateForm.payout) payload.payout = Number(updateForm.payout);
    if (updateForm.revenue) payload.revenue = Number(updateForm.revenue);
    if (updateForm.saleAmount) payload.saleAmount = Number(updateForm.saleAmount);
    if (updateForm.reason) payload.reason = updateForm.reason;
    updateMutation.mutate(payload as any);
  }

  function handleReset() {
    setOfferId("");
    setAffiliateId("");
    setStatus("");
    setTransactionId("");
  }

  const tabs: { id: TabType; label: string; icon: any }[] = [
    { id: "clicks", label: "Clicks", icon: MousePointer },
    { id: "conversions", label: "Conversions", icon: ShoppingCart },
    { id: "impressions", label: "Impressions", icon: Eye },
    { id: "invalid_clicks", label: "Invalid Clicks", icon: AlertTriangle },
    { id: "postback_logs", label: "Postback Logs", icon: Webhook },
    { id: "events", label: "Events", icon: Calendar },
  ];

  return (
    <div>
      <PageHeader title="Raw Reporting" description="Drill-down views for raw event data" />

      <div className="border-b border-gray-200 mb-6">
        <nav className="flex gap-0 -mb-px overflow-x-auto">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className={cn(
                  "inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap",
                  activeTab === tab.id ? "border-gray-900 text-gray-900" : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300",
                )}>
                <Icon className="w-4 h-4" /> {tab.label}
              </button>
            );
          })}
        </nav>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">From</label>
            <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">To</label>
            <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900" />
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Offer</label>
            <select value={offerId} onChange={(e) => setOfferId(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 max-w-[200px]">
              <option value="">All Offers</option>
              {offersList.map((o) => <option key={o.id} value={String(o.id)}>{o.name} (#{o.id})</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Affiliate</label>
            <select value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)}
              className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900 max-w-[200px]">
              <option value="">All Affiliates</option>
              {affiliatesList.map((a) => <option key={a.id} value={String(a.id)}>{a.name} (#{a.id})</option>)}
            </select>
          </div>
          {(activeTab === "conversions" || activeTab === "postback_logs") && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Txn ID</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input type="text" value={transactionId} onChange={(e) => setTransactionId(e.target.value)} placeholder="Search..."
                  className="rounded-lg border border-gray-300 px-3 pl-9 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 w-36" />
              </div>
            </div>
          )}
          {activeTab === "conversions" && (
            <div className="flex items-center gap-2">
              <label className="text-sm font-medium text-gray-700 whitespace-nowrap">Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)}
                className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-gray-900">
                <option value="">All</option>
                <option value="pending">Pending</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
              </select>
            </div>
          )}
          <div className="flex items-center gap-2 ml-auto">
            <button onClick={handleExport}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              <Download className="w-4 h-4" /> Export CSV
            </button>
            <button onClick={() => refetch()} disabled={isLoading}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50">
              <RefreshCw className={cn("w-4 h-4", isLoading && "animate-spin")} /> Refresh
            </button>
            <button onClick={handleReset}
              className="px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
              Reset
            </button>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-700">{(error as Error).message}</p>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
            <p className="text-gray-500 text-sm mt-3">Loading {activeTab}...</p>
          </div>
        ) : data.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-500 text-sm">No data found for the selected filters</p>
            <p className="text-gray-400 text-xs mt-1">Try expanding the date range or removing filters</p>
          </div>
        ) : (
          <>
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200 text-xs text-gray-500">
              Showing {data.length} of {totalRows} {activeTab.replace("_", " ")}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    {activeTab === "clicks" && (
                      <><Th>Time</Th><Th>Offer</Th><Th>Affiliate</Th><Th>Country</Th><Th>Device</Th><Th>OS</Th><Th>Browser</Th><Th>IP</Th><Th>Sub1</Th><Th>Source</Th><Th>Unique</Th></>
                    )}
                    {activeTab === "conversions" && (
                      <><Th>Time</Th><Th>Offer</Th><Th>Affiliate</Th><Th>Transaction ID</Th><Th>Status</Th><Th>Payout</Th><Th>Revenue</Th><Th>Sale Amt</Th><Th>Country</Th><Th>Event</Th><Th>Actions</Th></>
                    )}
                    {activeTab === "impressions" && (
                      <><Th>Time</Th><Th>Offer</Th><Th>Affiliate</Th><Th>Country</Th><Th>Device</Th></>
                    )}
                    {activeTab === "invalid_clicks" && (
                      <><Th>Time</Th><Th>Offer</Th><Th>Affiliate</Th><Th>Country</Th><Th>Device</Th><Th>IP</Th><Th>Bot</Th><Th>Proxy</Th><Th>User Agent</Th></>
                    )}
                    {activeTab === "postback_logs" && (
                      <><Th>Time</Th><Th>Offer</Th><Th>Affiliate</Th><Th>Postback ID</Th><Th>Txn ID</Th><Th>HTTP Status</Th><Th>Success</Th><Th>Time (ms)</Th><Th>Error</Th></>
                    )}
                    {activeTab === "events" && (
                      <><Th>Event Name</Th><Th>Count</Th><Th>Total Payout</Th><Th>Total Revenue</Th><Th>Total Sale</Th><Th>First</Th><Th>Last</Th></>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {activeTab === "clicks" && data.map((row: any, i: number) => (
                    <tr key={row.event_id || i} className="border-b border-gray-100 hover:bg-gray-50">
                      <Td>{row.created_at ? formatDateTime(row.created_at) : "-"}</Td>
                      <Td>{row.offer_id ?? "-"}</Td><Td>{row.affiliate_id ?? "-"}</Td>
                      <Td>{row.country_code ?? "-"}</Td><Td>{row.device_type ?? "-"}</Td>
                      <Td>{row.os ?? "-"}</Td><Td>{row.browser ?? "-"}</Td>
                      <Td mono>{row.ip ?? "-"}</Td><Td>{row.sub1 ?? "-"}</Td>
                      <Td>{row.source_id ?? "-"}</Td>
                      <Td>{row.is_unique ? <span className="text-green-600 font-medium">Yes</span> : <span className="text-gray-400">No</span>}</Td>
                    </tr>
                  ))}
                  {activeTab === "conversions" && data.map((row: any, i: number) => (
                    <tr key={row.event_id || i} className="border-b border-gray-100 hover:bg-gray-50">
                      <Td>{row.created_at ? formatDateTime(row.created_at) : "-"}</Td>
                      <Td>{row.offer_id ?? "-"}</Td><Td>{row.affiliate_id ?? "-"}</Td>
                      <Td mono>{row.transaction_id ?? "-"}</Td>
                      <Td><StatusBadge status={row.status} /></Td>
                      <Td>${Number(row.payout ?? 0).toFixed(2)}</Td>
                      <Td>${Number(row.revenue ?? 0).toFixed(2)}</Td>
                      <Td>${Number(row.sale_amount ?? 0).toFixed(2)}</Td>
                      <Td>{row.country_code ?? "-"}</Td>
                      <Td>{row.event_name ?? "-"}</Td>
                      <Td>
                        <button onClick={() => openUpdateDialog(row)} className="p-1 rounded hover:bg-gray-200 text-gray-500 hover:text-gray-700" title="Update conversion">
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                      </Td>
                    </tr>
                  ))}
                  {activeTab === "impressions" && data.map((row: any, i: number) => (
                    <tr key={row.event_id || i} className="border-b border-gray-100 hover:bg-gray-50">
                      <Td>{row.created_at ? formatDateTime(row.created_at) : "-"}</Td>
                      <Td>{row.offer_id ?? "-"}</Td><Td>{row.affiliate_id ?? "-"}</Td>
                      <Td>{row.country_code ?? "-"}</Td><Td>{row.device_type ?? "-"}</Td>
                    </tr>
                  ))}
                  {activeTab === "invalid_clicks" && data.map((row: any, i: number) => (
                    <tr key={row.event_id || i} className="border-b border-gray-100 hover:bg-gray-50">
                      <Td>{row.created_at ? formatDateTime(row.created_at) : "-"}</Td>
                      <Td>{row.offer_id ?? "-"}</Td><Td>{row.affiliate_id ?? "-"}</Td>
                      <Td>{row.country_code ?? "-"}</Td><Td>{row.device_type ?? "-"}</Td>
                      <Td mono>{row.ip ?? "-"}</Td>
                      <Td>{row.is_bot ? <span className="text-red-600 font-medium">Bot</span> : "-"}</Td>
                      <Td>{row.is_proxy ? <span className="text-orange-600 font-medium">Proxy</span> : "-"}</Td>
                      <Td><span className="text-xs truncate max-w-[200px] block">{row.user_agent ?? "-"}</span></Td>
                    </tr>
                  ))}
                  {activeTab === "postback_logs" && data.map((row: any, i: number) => (
                    <tr key={row.event_id || i} className="border-b border-gray-100 hover:bg-gray-50">
                      <Td>{row.created_at ? formatDateTime(row.created_at) : "-"}</Td>
                      <Td>{row.offer_id ?? "-"}</Td><Td>{row.affiliate_id ?? "-"}</Td>
                      <Td>{row.postback_id ?? "-"}</Td>
                      <Td mono>{row.transaction_id ?? "-"}</Td>
                      <Td><span className={cn("font-medium", Number(row.http_status) >= 200 && Number(row.http_status) < 300 ? "text-green-600" : "text-red-600")}>{row.http_status ?? "-"}</span></Td>
                      <Td>{row.is_success ? <span className="text-green-600">Yes</span> : <span className="text-red-600">No</span>}</Td>
                      <Td>{row.response_time_ms ?? "-"}</Td>
                      <Td><span className="text-xs text-red-600 truncate max-w-[150px] block">{row.error_message || "-"}</span></Td>
                    </tr>
                  ))}
                  {activeTab === "events" && data.map((row: any, i: number) => (
                    <tr key={row.event_name || i} className="border-b border-gray-100 hover:bg-gray-50">
                      <Td><span className="font-medium">{row.event_name ?? "-"}</span></Td>
                      <Td>{Number(row.event_count ?? 0).toLocaleString()}</Td>
                      <Td>${Number(row.total_payout ?? 0).toFixed(2)}</Td>
                      <Td>${Number(row.total_revenue ?? 0).toFixed(2)}</Td>
                      <Td>${Number(row.total_sale_amount ?? 0).toFixed(2)}</Td>
                      <Td>{row.first_event ? formatDateTime(row.first_event) : "-"}</Td>
                      <Td>{row.last_event ? formatDateTime(row.last_event) : "-"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Conversion Update Dialog */}
      {editingConversion && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Update Conversion</h3>
              <button onClick={() => setEditingConversion(null)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-xs text-gray-500 mb-4 font-mono">{editingConversion.transaction_id}</p>
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={updateForm.status} onChange={(e) => setUpdateForm((f) => ({ ...f, status: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm">
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Payout</label>
                  <input type="number" step="0.01" value={updateForm.payout} onChange={(e) => setUpdateForm((f) => ({ ...f, payout: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Revenue</label>
                  <input type="number" step="0.01" value={updateForm.revenue} onChange={(e) => setUpdateForm((f) => ({ ...f, revenue: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Sale Amt</label>
                  <input type="number" step="0.01" value={updateForm.saleAmount} onChange={(e) => setUpdateForm((f) => ({ ...f, saleAmount: e.target.value }))}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Reason</label>
                <input type="text" value={updateForm.reason} onChange={(e) => setUpdateForm((f) => ({ ...f, reason: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm" placeholder="Optional reason for update" />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button onClick={() => setEditingConversion(null)} className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={submitUpdate} disabled={updateMutation.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 disabled:opacity-50">
                <Check className="w-4 h-4" /> {updateMutation.isPending ? "Updating..." : "Update"}
              </button>
            </div>
            {updateMutation.isError && (
              <p className="mt-2 text-sm text-red-600">{(updateMutation.error as Error).message}</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return <th className="text-left text-xs font-medium text-gray-500 uppercase px-4 py-3">{children}</th>;
}

function Td({ children, mono }: { children: React.ReactNode; mono?: boolean }) {
  return <td className={cn("px-4 py-3 text-xs text-gray-700", mono && "font-mono")}>{children}</td>;
}

function StatusBadge({ status }: { status?: string }) {
  if (!status) return <span>-</span>;
  return (
    <span className={cn("px-2 py-0.5 rounded text-xs font-medium",
      status === "approved" && "bg-green-100 text-green-700",
      status === "rejected" && "bg-red-100 text-red-700",
      status === "pending" && "bg-yellow-100 text-yellow-700",
    )}>{status}</span>
  );
}
